import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';

import { useGetDenominationsTypes } from '@/api/hooks/denimonationHooks';
import { useGetProgramsTypes } from '@/api/hooks/programHooks';
import { useGetReligionsTypes } from '@/api/hooks/religionHooks';
import { InterestsData, interestsResolver } from '@/lib/validations/AuthSchema';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import CheckboxAccordionGroup from '../inputs/CheckboxAccordionGroup';
import Text from '../Text';

interface PreferencesFormProps {
  defaultValues: InterestsData;
  onDone: (data: InterestsData) => void;
  isPending: boolean;
  maxPrograms?: number;
  showReligions?: boolean;
  showDenominations?: boolean;
  submitLabel?: string;
}

const PreferencesForm = ({
  defaultValues,
  onDone,
  isPending,
  maxPrograms,
  showReligions = true,
  showDenominations = true,
  submitLabel = i18n.t('mix.next')
}: PreferencesFormProps) => {
  const { data: religions, isLoading: isLoadingReligions } = useGetReligionsTypes();
  const { data: focusArea, isLoading: isLoadingFocusArea } = useGetProgramsTypes();
  const { data: denominations, isLoading: isLoadingDenomination } = useGetDenominationsTypes();

  const denominationsOptions =
    denominations?.map((item) => ({
      value: item.id,
      label: item.name
    })) ?? [];

  const religionsOptions =
    religions?.map((item) => ({
      value: item.id,
      label: item.name
    })) ?? [];

  const focusAreaOptions =
    focusArea?.data?.map((item) => ({
      value: item.id,
      label: item.name
    })) ?? [];

  const {
    control,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { isValid, errors },
    reset
  } = useForm<InterestsData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: interestsResolver as any
  });

  const onSubmit: SubmitHandler<InterestsData> = (data) => {
    onDone(data);
  };

  const watchedFocusedAreas = watch('programs');

  useEffect(() => {
    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  useEffect(() => {
    if (maxPrograms && watchedFocusedAreas && watchedFocusedAreas.length > maxPrograms) {
      setError('programs', {
        message: i18n.t('sign_up.nonProfit.wizard.errors.focusAreasLimit')
      });
    } else {
      clearErrors('programs');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxPrograms, watchedFocusedAreas]);

  return (
    <View style={styles.contentWrapper}>
      <View style={styles.formContainer}>
        {showReligions && (
          <>
            <CheckboxAccordionGroup
              name="religions"
              options={religionsOptions}
              control={control}
              showError={false}
              groupLabel={i18n.t('sign_in.individual.interests.religions')}
              disabled={isLoadingReligions}
            />
            <Divider />
          </>
        )}
        {showDenominations && (
          <>
            <CheckboxAccordionGroup
              name="denominations"
              options={denominationsOptions}
              control={control}
              showError={false}
              groupLabel={i18n.t('sign_in.individual.interests.denominations')}
              disabled={isLoadingDenomination}
            />
            <Divider />
          </>
        )}
        <CheckboxAccordionGroup
          name="programs"
          options={focusAreaOptions}
          control={control}
          showError={false}
          groupLabel={i18n.t('sign_in.individual.interests.focus_area')}
          description={maxPrograms === 5 ? i18n.t('sign_in.individual.interests.focus_area_description') : undefined}
          disabled={isLoadingFocusArea}
        />
        <Divider />
      </View>
      {errors.programs && <Text style={styles.errorText}>{errors?.programs?.message}</Text>}

      <BrandedButton
        loading={isPending}
        onPress={handleSubmit(onSubmit)}
        disabled={!isValid || errors.programs?.message !== undefined}>
        {submitLabel}
      </BrandedButton>
    </View>
  );
};

export default PreferencesForm;

const styles = StyleSheet.create({
  contentWrapper: {
    flex: 1
  },

  formContainer: {
    rowGap: 16,
    flex: 1,
    marginBottom: 20
  },

  errorText: {
    color: 'red',
    fontSize: 16,
    fontWeight: 'bold'
  }
});
